import { GTP_BET_BUILDER_COMBINATION_TYPE } from '~Betslip/uiModes/shared/GTPBetBuilder/constants';
import { BET_TRACKING_TYPE } from '~spa/Components/BetHistory/BetTracking/constants.js';

/**
 * Generic Subscription Store middleware for websocket message processing.
 *
 * Processes subscribe/unsubscribe websocket messages that contain array payloads
 * under specific properties (e.g., 'combinations', 'selection_filters'). The
 * middleware manages active subscriptions through the subscription store and returns
 * updated payloads with only the new/changed subscription data.
 *
 * Supported message types:
 * - `gtp_betbuilder`: Contains arrays of selection IDs in 'combinations' property
 * - `bet_tracking`: Contains objects with event_id/market_id pairs in 'selection_filters' property
 *
 * @example
 * // GTP Bet Builder subscription
 * const gtpPayload = {
 *     action: 'register',
 *     type: 'gtp_betbuilder',
 *     combinations: [
 *         [100, 200, 333],
 *         [56, 57]
 *     ]
 * };
 *
 * @example
 * // Bet Tracking subscription
 * const trackingPayload = {
 *     action: 'register',
 *     type: 'bet_tracking',
 *     selection_filters: [
 *         { event_id: 1, market_id: 1 },
 *         { event_id: 2, market_id: 2 }
 *     ]
 * }
 *
 * @param {Object} wsPayload - The websocket message payload to process (see examples above)
 * @param {string} wsPayload.type - The message type (determines which property to process)
 * @param {string} wsPayload.action - The action to perform ('register' or 'unregister')
 * @param {Array} [wsPayload.combinations] - Array of selection ID arrays (for gtp_betbuilder)
 * @param {Array} [wsPayload.selection_filters] - Array of event/market objects (for bet_tracking)
 * @param {Object} options - Additional options passed to the middleware (e.g. subscriptionStore)
 * @returns {Object} Updated websocket payload with new subscription data, or original payload if unchanged
 * @throws {Error} When the message type is not recognized
 *
 * @note Add new cases to the `switch (type)` to support additional message types.
 *
 * @note When you write your middleware you should check that it is only operating on intended
 * messages. If the message type is not the intended one then return it as is. E.g.:
 *
 *  myBasicMiddleware => (message, { subscriptionStore }) => {
 *      if (message.type !== 'my-type') {
 *          return message;
 *      }
 *
 *      // your logic
 *
 *      return updatedMessage; // OR false to prevent message from being sent at all
 * }
 */
export const sldGenericSubscriptionStoreMiddleware = (wsPayload, { subscriptionStore }) => {
    const { type, action } = wsPayload;
    if (!type || !action) {

        return wsPayload;
    }

    let idGroups = [];
    let payloadKey = '';

    switch (type) {
        case GTP_BET_BUILDER_COMBINATION_TYPE:
            payloadKey = 'combinations';
            idGroups = wsPayload[payloadKey];

            break;
        case BET_TRACKING_TYPE:
            payloadKey = 'selection_filters';
            idGroups = wsPayload[payloadKey];

            break;
        default:

            throw new Error(`wsMessage type "${type}" is not recognised...`);
    }

    let newIdGroups = [];
    switch (action) {
        case 'register':
            newIdGroups = subscriptionStore.subscribe({ type, idGroups });

            break;
        case 'unregister':
            newIdGroups = subscriptionStore.unsubscribe({ type, idGroups });

            break;
        default:
            break;
    }

    return {
        ...wsPayload,
        [payloadKey]: newIdGroups,
    };
};
